import React, { useEffect, useReducer } from "react";
import styled from "@emotion/styled";
import {
  ThemeProvider,
  StyledEngineProvider,
  Box,
  useMediaQuery,
} from "@mui/material";
import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { Route, BrowserRouter } from "react-router-dom";

import { routes } from "./routes";
import { AppsProvider } from "./appContext";
import { reducer, initReducer } from "./appReducer";
import NavBar from "./Navbar";
import { DRAWER_WIDTH } from "./tools";
import Clients from "./User/Clients";
import Poll from "./Poll";
import getTheme from "./theme";
import LoadingScreen from "./Components/LoadingScreen";
import ErrorBoundary from "./ErrorBoundary";
import useScript from "./Components/useScript";
import useAccountSettings from "./ManageAccount/useAccountSettings";

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up("lg")]: {
    marginLeft: DRAWER_WIDTH,
  },
}));

export default function App() {
  const [app, dispatch] = useReducer(reducer, initReducer); // global state
  // get script version
  const { jsLastUpdate } = useAccountSettings({ sessionId: app.sessionId });
  // load custom scripts
  const { loading: isNormingLoading } = useScript(
    `${window.NORMING_ADDRESS}?v=${jsLastUpdate}`
  );
  const { loading: isMyLibraryLoading } = useScript(
    `${window.MYLIBRARY_ADDRESS}?v=${jsLastUpdate}`
  );
  const { loading: isMavaLoading } = useScript(
    `${window.MAVA_ADDRESS}?v=${jsLastUpdate}`
  );
  useScript(window.WORD_FREQ_ADDRESS);

  // initialise app
  useEffect(() => {
    let sessionId = null;
    // get session id from url search params
    sessionId =
      new URLSearchParams(document.location.search).get("sessionId") ?? null;
    // if sessionId is not in the search params, get sessionId from the localstorage
    if (sessionId === null) {
      sessionId = localStorage.getItem("sessionId");
    }
    // logout user if no session id is defined
    if (sessionId === null && window.REACT_APP_LOGOUT_ADDRESS !== undefined) {
      window.location = window.REACT_APP_LOGOUT_ADDRESS;
    }
    // store the session id
    if (sessionId !== null) {
      // save the sessionId in "global" state
      dispatch({ type: "SET_SESSION_ID", payload: sessionId });
      // save the sessionId in localStorage
      localStorage.setItem("sessionId", sessionId);
    }
  }, []);

  const theme = responsiveFontSizes(
    createTheme(getTheme({ darkState: app.darkState }))
  );
  const mobile = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <AppsProvider value={{ app, dispatch }}>
          {app.sessionId === null ||
          isNormingLoading ||
          isMyLibraryLoading ||
          isMavaLoading ? (
            <LoadingScreen />
          ) : (
            <Main>
              <BrowserRouter basename={window.REACT_APP_BASE_PATH}>
                <Poll />
                <NavBar />
                <Clients />
                <ErrorBoundary>
                  {routes.map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      render={(props) => <route.component {...props} />}
                    />
                  ))}
                </ErrorBoundary>
              </BrowserRouter>
              {mobile ? <Box height={DRAWER_WIDTH} /> : null}
            </Main>
          )}
        </AppsProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
